import { 
  Firestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  writeBatch, 
  DocumentReference,
  serverTimestamp,
  Timestamp,
  getDoc,
  orderBy,
  limit as firestoreLimit
} from "firebase/firestore";
import { prepareOfferData } from "./offerDataPreparation";
import { toast } from "sonner";
import { Offer } from "@/types/offer";
import { CampLocation } from "@/types/campLocation";
import { normalizeSessionOverrideFieldsForPublish } from "@/functions-shared/offer/normalizeSessionOverrideFieldsForPublish";
import { overridesForRebuild } from "@/functions-shared/offer/storedSessionPrice";
import { releaseOldProgramWindow } from "@/functions-shared/offer/storedSessionWindow";
import { readPriorPublishedVariations, priorProgramTimingsFor } from "@/functions-shared/offer/priorPublishedProgram";
import { isLocationEditLiveSource } from "@/config/locationEditFlag";
import { ensurePlaceholderOffersForLocation } from "./placeholderOfferService";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/services/firebaseService";

/**
 * Updates all offers/sessions for a location to ensure derived fields are in sync with
 * the latest data from campOrganizations and campLocations collections.
 * 
 * @param db Firestore instance
 * @param locationId ID of the location to update offers for
 * @param userId ID of the user performing the update
 * @param priorVariations KB-4564 ruling B — the location's variations as they were BEFORE the save that
 *   triggered this propagation, when the caller has them. An offer's window group equal to what its OLD
 *   program produced then follows a re-time. Omitted ("Publish to offers"), they are read from version history
 *   — the version published before the current one (ruling B-hist, priorPublishedProgram.ts) — and where that
 *   cannot be known safely, every window an offer holds is carried.
 * @returns Promise with success status and count of updated offers
 */
export const updateAllOffersForLocation = async (
  db: Firestore,
  locationId: string,
  userId: string,
  priorVariations?: ReadonlyArray<{ key?: unknown; timings?: unknown }> | null
): Promise<{ success: boolean; updatedCount: number }> => {
  if (!locationId) {
    console.error("Location ID is required to update offers");
    throw new Error("Location ID is required");
  }

  if (!userId) {
    console.error("User ID is required to update offers");
    throw new Error("User ID is required");
  }

  console.log(`Starting batch update for location: ${locationId}, requested by user: ${userId}`);

  // Track missing variation keys to show each error only once
  const shownMissingKeys = new Set<string>();

  try {
    // Ensure placeholders are synchronized before updating real offers
    try {
      const pl = await ensurePlaceholderOffersForLocation(db, locationId, userId);
      console.log(`[updateAllOffersForLocation] Placeholder sync - created: ${pl.created}, updated: ${pl.updated}, deleted: ${pl.deleted}`);
    } catch (e) {
      console.error("[updateAllOffersForLocation] Placeholder sync failed", e);
    }

    // Create a reference to the location document
    const locationRef = doc(db, "campLocations", locationId);
    // Determine if location workflow is external-facing (wf_external*)
    let isExternalWorkflow = false;
    let liveLocation: Record<string, unknown> | null = null;
    try {
      const locSnap = await getDoc(locationRef);
      const locData: any = locSnap.exists() ? locSnap.data() : {};
      liveLocation = locSnap.exists() ? locData : null;
      const wf: string = typeof locData?.workflowStatus === "string" ? locData.workflowStatus : "";
      isExternalWorkflow = wf.startsWith("wf_external");
    } catch {
      isExternalWorkflow = false;
    }

    // KB-4564 ruling B-hist — with no pre-save programs from the caller, the programs as published BEFORE the
    // current publish: the existing published-version read (getPublishedVersion's query shape, served by the kb-ff
    // index on locationRef + status + timestamp desc), limit 2. The KB-3308 live-source path writes no versions, so
    // it is unknown outright. Any failure is unknown (every held window carried); never throws.
    const programsBefore = priorVariations ?? (isLocationEditLiveSource()
      ? undefined
      : await readPriorPublishedVariations(
          liveLocation,
          async () => {
            const snap = await getDocs(query(
              collection(db, "campLocationVersions"),
              where("locationRef", "==", locationRef),
              where("status", "==", "published"),
              orderBy("timestamp", "desc"),
              firestoreLimit(2)
            ));
            return snap.docs.map((d) => ({ id: d.id, variations: (d.data() as any)?.data?.variations }));
          },
          (msg, err) => console.warn(msg, err)
        ));
    
    // 🔥 CRITICAL DEBUG: Log the exact query being performed
    console.log(`🔍 [DEBUG] Creating locationRef for query:`, locationRef);
    console.log(`🔍 [DEBUG] LocationRef path:`, locationRef.path);
    console.log(`🔍 [DEBUG] LocationRef id:`, locationRef.id);
    
    // Query offers for this location whose endDate is within the last 35 days (inclusive) or in the future
    const offersRef = collection(db, "offers");
    const now = new Date();
    const thirtyFiveDaysMs = 35 * 24 * 60 * 60 * 1000;
    const cutoff = new Date(now.getTime() - thirtyFiveDaysMs);
    let offersSnapshot;
    try {
      // Prefer server-side filtering to reduce reads and include recent past sessions
      const q = query(
        offersRef,
        where("locationRef", "==", locationRef),
        where("endDate", ">=", cutoff)
      );
      console.log(`🔍 [DEBUG] Executing filtered query for offers (endDate >= cutoff=${cutoff.toISOString()}) with locationRef == ${locationRef.path}`);
      offersSnapshot = await getDocs(q);
    } catch (err: any) {
      // Likely missing composite index; fall back to client-side filtering
      console.warn("[updateAllOffersForLocation] Filtered query failed; falling back to client-side filtering.", err?.code || err);
      const q = query(offersRef, where("locationRef", "==", locationRef));
      console.log(`🔍 [DEBUG] Executing unfiltered query for offers with locationRef == ${locationRef.path}`);
      offersSnapshot = await getDocs(q);
    }
    
    // Also include legacy offers keyed by string locationId (older schema).
    let legacySnapshot;
    try {
      const qLegacy = query(
        offersRef,
        where("locationId", "==", locationId),
        where("endDate", ">=", cutoff)
      );
      console.log(`🔍 [DEBUG] Executing legacy filtered query for offers (endDate >= cutoff=${cutoff.toISOString()}) with locationId == ${locationId}`);
      legacySnapshot = await getDocs(qLegacy);
    } catch (err: any) {
      console.warn("[updateAllOffersForLocation] Legacy filtered query failed; falling back to locationId-only.", err?.code || err);
      const qLegacy = query(offersRef, where("locationId", "==", locationId));
      console.log(`🔍 [DEBUG] Executing legacy unfiltered query for offers with locationId == ${locationId}`);
      legacySnapshot = await getDocs(qLegacy);
    }

    // Offers with locationRef stored as a path string (not a DocumentReference)
    const stringLocationRefPath = `campLocations/${locationId}`;
    let stringRefDocs: typeof offersSnapshot.docs = [];
    try {
      const qStringRef = query(
        offersRef,
        where("locationRef", "==", stringLocationRefPath),
        where("endDate", ">=", cutoff)
      );
      console.log(
        `[updateAllOffersForLocation] Querying string locationRef == ${stringLocationRefPath} (endDate >= cutoff)`
      );
      const stringRefSnapshot = await getDocs(qStringRef);
      stringRefDocs = stringRefSnapshot.docs;
    } catch (err: any) {
      console.warn(
        "[updateAllOffersForLocation] String locationRef filtered query failed; falling back to string ref only.",
        err?.code || err
      );
      try {
        const qFallback = query(offersRef, where("locationRef", "==", stringLocationRefPath));
        const stringRefSnapshot = await getDocs(qFallback);
        stringRefDocs = stringRefSnapshot.docs;
      } catch (err2: any) {
        console.warn("[updateAllOffersForLocation] String locationRef query failed.", err2?.code || err2);
      }
    }

    const merged = new Map<string, (typeof offersSnapshot.docs)[number]>();
    offersSnapshot.docs.forEach((d) => merged.set(d.id, d));
    legacySnapshot.docs.forEach((d) => merged.set(d.id, d));
    stringRefDocs.forEach((d) => merged.set(d.id, d));
    const mergedDocs = Array.from(merged.values());

    console.log(
      `🔍 [DEBUG] Query returned ${offersSnapshot.size} by ref; ${legacySnapshot.size} by locationId; ${stringRefDocs.length} by string locationRef; merged total ${mergedDocs.length}`
    );
    
    if (mergedDocs.length === 0) {
      console.log(`No offers found for location ${locationId}`);
      
      // 🔥 CRITICAL DEBUG: Let's try a broader query to see what offers exist
      const allOffersSnapshot = await getDocs(collection(db, "offers"));
      console.log(`🔍 [DEBUG] Total offers in database: ${allOffersSnapshot.size}`);
      
      if (allOffersSnapshot.size > 0) {
        console.log(`🔍 [DEBUG] Sample of existing offers:`);
        allOffersSnapshot.docs.slice(0, 3).forEach(doc => {
          const data = doc.data();
          console.log(`🔍 [DEBUG] Offer ${doc.id} locationRef:`, data.locationRef);
          console.log(`🔍 [DEBUG] Offer ${doc.id} locationRef type:`, typeof data.locationRef);
          if (data.locationRef?.path) {
            console.log(`🔍 [DEBUG] Offer ${doc.id} locationRef path:`, data.locationRef.path);
          }
        });
      }
      
      return { success: true, updatedCount: 0 };
    }
    
    // Build list of docs to process (offers ending on/after cutoff)
    const docsToProcess = mergedDocs.filter((offerDoc) => {
      try {
        const data: any = offerDoc.data();
        const rawEnd = data?.endDate;
        const endDate: Date | null = rawEnd instanceof Timestamp
          ? rawEnd.toDate()
          : (rawEnd instanceof Date ? rawEnd : null);
        if (!endDate) return false; // Only publish offers with a valid endDate
        return endDate.getTime() >= cutoff.getTime();
      } catch (e) {
        return false;
      }
    });

    console.log(`Found ${docsToProcess.length} offers since ${cutoff.toISOString()} to update for location ${locationId}`);

    let locationDataForNormalize: CampLocation | null = null;
    const decimalEnums: Record<string, { value?: number }> = {};
    if (docsToProcess.length > 0) {
      const locationDocSnap = await getDoc(locationRef);
      locationDataForNormalize = locationDocSnap.exists() ? (locationDocSnap.data() as CampLocation) : null;
      if (!locationDataForNormalize) {
        throw new Error(`Location ${locationId} not found`);
      }
      const enumDocs = await getDocs(collection(db, "decimalEnums"));
      enumDocs.docs.forEach((d) => {
        const data = d.data() as { value?: number };
        decimalEnums[d.id] = { value: data.value };
      });
    }

    // Create a batch for efficient updates
    const batch = writeBatch(db);
    let updatedCount = 0;
    
    // Process each offer
    for (const offerDoc of docsToProcess) {
      const offerId = offerDoc.id;
      // KB-4564 ruling B — when the caller passed the pre-save variations, a window group that merely copied the
      // offer's OLD program is released here, so the rebuild's carry hands it the NEW program's window. A program
      // the old list lacks (new since) is unknown, and the doc is left as stored (every held group carried).
      const offerData = releaseOldProgramWindow(
        offerDoc.data() as Record<string, unknown>,
        priorProgramTimingsFor(programsBefore, (offerDoc.data() as Offer).variationKey)
      ) as unknown as Offer;

      console.log(`Processing offer: ${offerId}`);
      
      // 🔥 CRITICAL DEBUG: Log current offer's searchResultExcerpt value
      const currentSearchResultExcerpt = offerData.locationData?.searchResultExcerpt;
      console.log(`🔍 [DEBUG] Current offer ${offerId} searchResultExcerpt: "${currentSearchResultExcerpt}"`);
      
      try {
        const variation = locationDataForNormalize!.variations?.find(
          (v) => v.key === offerData.variationKey
        );
        const normalizedOverrides = variation
          ? overridesForRebuild(offerData as unknown as Record<string, unknown>, normalizeSessionOverrideFieldsForPublish({
              offer: {
                numDaysOverride: offerData.numDaysOverride ?? null,
                priceOverride: offerData.priceOverride ?? null,
                priceNoteOverride: (offerData as any).priceNoteOverride ?? null,
                timingNoteOverride: (offerData as any).timingNoteOverride ?? null,
                pricingTierPriceOverrides: (offerData as any).pricingTierPriceOverrides ?? null,
                myOfferIncrementKey: offerData.myOfferIncrementKey ?? null,
              },
              variation,
              decimalEnums,
            }))
          : {
              numDaysOverride: offerData.numDaysOverride ?? null,
              priceOverride: offerData.priceOverride ?? null,
              priceNoteOverride: (offerData as any).priceNoteOverride ?? null,
              timingNoteOverride: (offerData as any).timingNoteOverride ?? null,
              pricingTierPriceOverrides: (offerData as any).pricingTierPriceOverrides ?? null,
              myOfferIncrementKey: offerData.myOfferIncrementKey ?? null,
            };

        const offerForPrepare = {
          ...offerData,
          ...normalizedOverrides,
        };

        // Prepare updated offer data with all derived fields recalculated
        const updatedOfferData = await prepareOfferData(
          db,
          locationId,
          offerForPrepare,
          userId,
          // KB-3733 — a batch catalog propagation must not re-price a camp-data session.
          offerData as unknown as Record<string, unknown>
        );
        
        // Remove id property as it shouldn't be part of the document data
        const { id, ...dataToUpdate } = updatedOfferData;
        
        // 🔥 CRITICAL DEBUG: Log the new searchResultExcerpt value that will be written
        const newSearchResultExcerpt = dataToUpdate.locationData?.searchResultExcerpt;
        console.log(`🔍 [DEBUG] Offer ${offerId} will be updated with searchResultExcerpt: "${newSearchResultExcerpt}"`);
        
        // Convert any undefined values to null for Firestore compatibility
        Object.entries(dataToUpdate).forEach(([key, value]) => {
          if (value === undefined) {
            console.log(`Converting undefined ${key} to null in offer ${offerId}`);
            (dataToUpdate as any)[key] = null;
          }
        });
        
        // Add firstAvailableAt if this is the first publish to offers AND location is external-facing
        try {
          const alreadySet = (offerData as any)?.firstAvailableAt != null;
          if (isExternalWorkflow && !alreadySet) {
            (dataToUpdate as any).firstAvailableAt = serverTimestamp();
          }
        } catch {
          // no-op
        }

        // Add document update to batch
        const offerRef = doc(db, "offers", offerId);
        batch.update(offerRef, dataToUpdate);
        updatedCount++;
        
      } catch (error: any) {
        console.error(`Error preparing data for offer ${offerId}:`, error);
        
        // Check if this is a missing variation key error
        if (error.missingVariationKey && error.locationId && !shownMissingKeys.has(error.missingVariationKey)) {
          // Mark this key as shown to prevent duplicate messages
          shownMissingKeys.add(error.missingVariationKey);
          
          // Extract latest version ID (could be string or object with id)
          let latestVersionId = error.latestVersionId;
          if (typeof latestVersionId === 'object' && latestVersionId?.id) {
            latestVersionId = latestVersionId.id;
          }
          
          // Show persistent toast message with exact format requested
          toast.error(
            `Error publishing offers; old data format. Report this error message to Support: campLocations/${error.locationId} and campLocationVariations/${latestVersionId} needs key '${error.missingVariationKey}' added to variations`,
            {
              duration: Infinity, // Never auto-close
              action: {
                label: "✕",
                onClick: () => {} // User must click X to close
              }
            }
          );
        }
        
        // Continue with other offers rather than failing the whole batch
      }
    }
    
    // Commit the batch
    if (updatedCount > 0) {
      await batch.commit();
      console.log(`Successfully updated ${updatedCount} offers for location ${locationId}`);
    } else {
      console.log(`No offers were updated for location ${locationId}`);
    }
    
    return {
      success: true,
      updatedCount
    };
    
  } catch (error) {
    console.error(`Error updating offers for location ${locationId}:`, error);
    throw error;
  }
};

// Server-side publish using callable Cloud Function
export const publishAllOffersForLocationServer = async (
  locationId: string
): Promise<{ success: boolean; updatedCount: number; skippedInSyncCount: number }> => {
  const functions = getFunctions(app, "us-east4");
  const fn = httpsCallable(functions, "publishOffersForLocation");
  const res = await fn({ locationId });
  const data = (res?.data || {}) as any;
  return {
    success: true,
    updatedCount: Number(data.updatedCount || 0),
    skippedInSyncCount: Number(data.skippedInSyncCount || 0),
  };
};

/**
 * Updates selected offers with the same field values
 * 
 * @param db Firestore instance
 * @param offerIds Array of offer IDs to update
 * @param updateData Object containing the fields to update
 * @param userId ID of the user performing the update
 * @returns Promise with success status and count of updated offers
 */
export const bulkUpdateOffers = async (
  db: Firestore,
  offerIds: string[],
  updateData: Partial<Offer>,
  userId: string
): Promise<{ success: boolean; updatedCount: number }> => {
  if (!offerIds.length) {
    console.error("No offer IDs provided for bulk update");
    return { success: false, updatedCount: 0 };
  }

  if (!userId) {
    console.error("User ID is required to update offers");
    throw new Error("User ID is required");
  }

  console.log(`Starting bulk update for ${offerIds.length} offers, requested by user: ${userId}`);
  console.log("Update data:", updateData);

  try {
    // Create a batch for efficient updates
    const batch = writeBatch(db);
    let updatedCount = 0;
    
    // Create a reference to the user document
    const userRef = userId ? doc(db, "users", userId) : null;
    
    // Add timestamp and user reference to update data
    const finalUpdateData = {
      ...updateData,
      lastEditedAt: serverTimestamp(),
      lastEditedBy: userRef
    };

    // If availabilityStatus is included in the bulk update, refresh avStatusAsOf
    if (Object.prototype.hasOwnProperty.call(updateData, 'availabilityStatus')) {
      (finalUpdateData as any).avStatusAsOf = serverTimestamp();
    }
    
    // Process each offer ID
    for (const offerId of offerIds) {
      try {
        // Add document update to batch
        const offerRef = doc(db, "offers", offerId);
        batch.update(offerRef, finalUpdateData);
        updatedCount++;
      } catch (error) {
        console.error(`Error preparing data for offer ${offerId}:`, error);
        // Continue with other offers rather than failing the whole batch
      }
    }
    
    // Commit the batch if there are updates to make
    if (updatedCount > 0) {
      await batch.commit();
      console.log(`Successfully updated ${updatedCount} offers`);
    } else {
      console.log(`No offers were updated`);
    }
    
    return {
      success: true,
      updatedCount
    };
    
  } catch (error) {
    console.error(`Error bulk updating offers:`, error);
    throw error;
  }
};
